import { registerServiceRequest } from "../slices/serviceRequestSlice";
import { logErrorEvent } from "../services/logService";

/**
 * Fetch a ServiceRequest by ID
 * - Checks Redux store first
 * - Fetches from FHIR server if missing
 * - Registers into Redux
 */
export const fetchServiceRequest = async (
    serviceRequestId,
    fhirClient,
    store,
    { forceFetch = false } = {}
) => {
    console.log("fetching ServiceRequest by id");

    if (!serviceRequestId || !fhirClient) return null;

    // 🧠 Check Redux cache
    const cached = store.getState().serviceRequests?.[serviceRequestId];
    if (cached && !forceFetch) {
        console.log(`🧠 ServiceRequest ${serviceRequestId} served from cache`);
        return cached;
    }

    try {
        console.log(`🌐 Fetching ServiceRequest/${serviceRequestId} from FHIR`);
        const fetched = await fhirClient.request(`ServiceRequest/${serviceRequestId}`);

        if (fetched?.resourceType !== "ServiceRequest") {
            console.warn("⚠️ Unexpected ServiceRequest response:", fetched);
            return null;
        }

        store.dispatch(registerServiceRequest(fetched));
        return fetched;

    } catch (err) {
        console.error("❌ Failed to fetch ServiceRequest:", err);

        const status = err?.response?.status || err?.status || 500;

        logErrorEvent({
            errorType: "read",
            error: {
                message: err?.message || "ServiceRequest fetch failed",
                status,
                resourceId: `ServiceRequest/${serviceRequestId}`
            },
            description: `Failed to retrieve ServiceRequest/${serviceRequestId} from FHIR server`,
            store
        });

        // Standardized error return
        return { error: status };
    }
};

/**
 * Fetch a ServiceRequest given a FHIR reference string
 * e.g. "ServiceRequest/123" or "ServiceRequest/123/_history/4"
 */
export const fetchServiceRequestByReference = async (
    ref,
    fhirClient,
    store,
    options = {}
) => {
    console.log("fetching ServiceRequest by Reference....................");

    if (!ref || typeof ref !== "string") {
        console.warn("⚠️ Invalid ServiceRequest reference (not a string):", ref);
        return null;
    }

    if (!ref.startsWith("ServiceRequest/")) {
        console.warn("⚠️ Reference is not a ServiceRequest:", ref);
        return null;
    }

    // Safely extract ID (handles versioned refs)
    const parts = ref.split("/");
    const serviceRequestId = parts[1];

    if (!serviceRequestId || serviceRequestId === "undefined" || serviceRequestId === "null") {
        console.warn("⚠️ Could not extract valid ServiceRequest ID from reference:", ref);
        return null;
    }

    return fetchServiceRequest(serviceRequestId, fhirClient, store, options);
};
